/**
 * Shared layout for /profiles/:id, /vaccination/:id, /growth/:id,
 * /milestones/:id. Owns the profile fetch + ChildProfileHeader so the
 * header never re-mounts when switching tabs, and provides the loaded
 * profile to child pages via context (children don't re-fetch).
 *
 * Each child registers its own PDF exporter at mount via setPdfExporter;
 * the header's "Export PDF" button calls the active exporter.
 */
import React, { createContext, useContext, useRef } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { FiDownload } from 'react-icons/fi';
import { PageButton } from '../../theme/components/PageButton';
import { useProfile } from '../../hooks/useProfile';
import { ChildProfileHeader } from './ChildProfileHeader';

const ChildProfileContext = createContext(null);

export function useChildProfile() {
    const ctx = useContext(ChildProfileContext);
    if (!ctx) {
        throw new Error('useChildProfile must be used inside ChildProfileLayout');
    }
    return ctx;
}

export default function ChildProfileLayout() {
    const { id, tab } = useParams();
    const profileState = useProfile(id);
    const pdfExporterRef = useRef(null);
    const setPdfExporter = (fn) => {
        pdfExporterRef.current = typeof fn === 'function' ? fn : null;
    };

    return (
        <ChildProfileContext.Provider
            value={{ id, ...profileState, setPdfExporter }}
        >
            <div className="vacc-wrapper">
                <ChildProfileHeader
                    profile={profileState.data}
                    active={tab === 'profiles' ? 'profile' : tab || 'profile'}
                    actions={
                        <PageButton
                            type="button"
                            variant="secondary"
                            startIcon={<FiDownload size={14} />}
                            onClick={() => pdfExporterRef.current?.()}
                        >
                            Export PDF
                        </PageButton>
                    }
                />
                <Outlet />
            </div>
        </ChildProfileContext.Provider>
    );
}